import { Fragment, ReactNode, useEffect, useRef } from 'react';
import { useModal } from 'react-modal-hook';
import { graphql, useStaticQuery } from 'gatsby';

import useOnScreen from 'utils/hooks/use-onscreen';

import Button from 'components/buttons/button';
import StructuredData from 'components/content-blocks/partials/structured-data';
import { SignUpPlanIds } from 'components/forms/constants';
import Link from 'components/link/link';
import ScheduleDemoModal from 'components/modal/schedule-demo/schedule-demo-modal';

import './styles/default.scss';
import Accordion from '../accordion/faq';
import Wysiwyg from '../content-blocks/partials/wysiwyg';
import StaticFaq from '../content-blocks/static-faq';
import CRMForm from '../forms/crm-form';
import ModalSignup from '../modal/signups';

type Props = {
  customHubspotMeetingButtonText?: string,
  customHubspotMeetingUrl?: string,
  faq?: any,
  faqStructuredData?: any,
  footerButtonLink?: string,
  footerCtaTitle?: ReactNode,
  footerSignupButtonText?: ReactNode,
  footerSignupHeader?: ReactNode,
  hideSignup?: boolean,
  hideSignupBox?: boolean,
  legalText?: ReactNode,
  modalTitle?: ReactNode,
  setIsFooterVisible?: (isVisible: boolean) => void,
  showCustomCalendar?: boolean,
  showInitFields?: boolean,
  showModalButton?: boolean
  showScheduleDemoButton?: boolean,
  signUpRefPlanId?: SignUpPlanIds,
  signupModalLaunch?: string,
  signupModalSubmit?: string,
  trialLength?: number,
  useStaticFaq?: boolean
}

type FooterLink = {
  title: string
  url: string
}
type FooterLinksGroup = {
  links: {
    link: FooterLink
  }[]
  title: string
}
type FooterLinksColumn = [key: string, links: FooterLinksGroup]

const Footer = ({
  footerCtaTitle,
  faq,
  useStaticFaq,
  modalTitle,
  legalText,
  faqStructuredData,
  showInitFields,
  hideSignup,
  hideSignupBox,
  signupModalSubmit,
  showModalButton,
  showScheduleDemoButton,
  customHubspotMeetingUrl,
  customHubspotMeetingButtonText,
  showCustomCalendar,
  footerSignupHeader,
  footerSignupButtonText,
  footerButtonLink,
  signUpRefPlanId,
  setIsFooterVisible,
  trialLength,
  signupModalLaunch,
}:Props) => {

  const data = useStaticQuery(graphql`
    {
      allWordpressAcfOptions {
        edges {
          node {
            options {
              main_links {
                column1 {
                  title
                  links {
                    link {
                      title
                      url
                    }
                  }
                }
                column2 {
                  title
                  links {
                    link {
                      title
                      url
                    }
                  }
                }
                column3 {
                  links {
                    link {
                      title
                      url
                    }
                  }
                  title
                }
                column4 {
                  title
                  links {
                    link {
                      title
                      url
                    }
                  }
                }
              }
              industry_links {
                title
                columns {
                  column1 {
                    links {
                      link {
                        url
                        title
                      }
                    }
                  }
                  column2 {
                    links {
                      link {
                        title
                        url
                      }
                    }
                  }
                  column3 {
                    links {
                      link {
                        title
                        url
                      }
                    }
                  }
                  column4 {
                    links {
                      link {
                        title
                        url
                      }
                    }
                  }
                }
              }
              subscription {
                subscribe_header
                subscription_copy
              }
              contact_us {
                footer_heading
                footer_text
                sales_link {
                  title
                  url
                }
                support_link {
                  title
                  url
                }
              }
              download_mobile_apps {
                app_store {
                  app_store_url {
                    title
                    url
                  }
                  app_store_image {
                    url {
                      source_url
                      alt_text
                    }
                  }
                }
              }
              social_icons_v2 {
                facebook_icon {
                  icon_image_alt
                  icon_image {
                    url {
                      source_url
                    }
                  }
                  icon_url {
                    url
                  }
                }
                instagram_icon {
                  icon_image_alt
                  icon_url {
                    url
                  }
                  icon_image {
                    url {
                      source_url
                    }
                  }
                }
                linkedin_icon {
                  icon_image_alt
                  icon_url {
                    url
                  }
                  icon_image {
                    url {
                      source_url
                    }
                  }
                }
                twitter_icon {
                  icon_image_alt
                  icon_image {
                    url {
                      source_url
                    }
                  }
                  icon_url {
                    url
                  }
                }
              }
              legal_links {
                privacy_policy {
                  title
                  url
                }
                terms_of_service {
                  title
                  url
                }
              }
            }
          }
        }
      }
      allWordpressAcfOptions {
        edges {
          node {
            options {
              structured_data {
                default_site_data
              }
            }
          }
        }
      }
    }
  `);

  const element = useRef(null);
  const isVisible = useOnScreen(element);

  useEffect(() => {
    if (setIsFooterVisible) {
      setIsFooterVisible(isVisible);
    }
  }, [isVisible]);
  
  const pageContext = data.allWordpressAcfOptions.edges[0].node;
  const structuredData = data.allWordpressAcfOptions.edges[0].node.options.structured_data.default_site_data;
  const [showScheduleDemoModal, hideScheduleDemoModal] = useModal(() => (
    <ScheduleDemoModal isOpen={ true } onHide={ hideScheduleDemoModal } />
  ));
  
  return (
    <footer ref={ element } className="footer-default">
      {hideSignup ? null : (
        <Fragment>
          {hideSignupBox ? null : (
            <section
              className="footer-signup-section"
              data-section="footerSignupSection"
            >
              <div className="cta-box has-text-centered has-shadow-large">
                <h2 className="has-margin-bottom-md">
                  {footerSignupHeader ||
                  footerCtaTitle ||
                  'Start scheduling free!'}
                  {!footerSignupHeader && <span> No credit card required.</span>}
                </h2>
                {footerButtonLink && footerButtonLink ? (
                  <Button
                    type="link"
                    href={ footerButtonLink }
                    className="button-green is-medium"
                    text={ footerSignupButtonText }
                  />

                ) : (
                  <ModalSignup
                    modalTitle={ modalTitle }
                    showInitFields={ showInitFields }
                    showModalButton={ showModalButton }
                    showScheduleDemoButton={ showScheduleDemoButton }
                    customHubspotMeetingUrl={ customHubspotMeetingUrl }
                    customHubspotMeetingButtonText={ customHubspotMeetingButtonText }
                    showCustomCalendar={ showCustomCalendar }
                    legalText={ legalText }
                    uniqueId="signupsFooter"
                    fieldLayout="row"
                    buttonInline
                    footerSignupButtonText={ footerSignupButtonText }
                    signupModalSubmit={ signupModalSubmit }
                    buttonClasses={ !showInitFields && 'button button-green' }
                    showIcons={ false }
                    targetModule={ false }
                    isFixed={ false }
                    isZapier={ false }
                    refPlanId={ signUpRefPlanId }
                    trialLength={ trialLength }
                    ctaVariation='Footer CTA + modal'
                    signupModalLaunch={ signupModalLaunch }
                  />)}

              </div>
            </section>)}
          <section className="illustration-section">
            <img
              loading="lazy"
              src="https://marketing-assets.wheniwork-production.com/2020/01/16143317/footer-group-illustration-optimized.svg"
              alt="drawing of employees at work cheering and waving"
              width="801"
              height="265"
            />
          </section>
          {faq && (
            <section className="faq-section">
              {useStaticFaq === true ? (
                <StaticFaq faq={ faq } />
              ) : (
                <Accordion faq={ faq } />
              )}
              {faqStructuredData && <StructuredData data={ faqStructuredData } />}
            </section>
          )}
        </Fragment>
      )}

      <section className="footer-section">
        <div className="container-large">
          <section
            className="footer-wrap"
            data-section="footer-main-navigation"
          >
            {Object.entries(pageContext.options.main_links).map(
              (column, i) => {
                const group = (column as FooterLinksColumn)[1];
                return (
                  <div key={ i } className="footer-col">
                    <h4 className="footer-heading">{group.title}</h4>
                    <ul>
                      {group.links.map(({ link }, index) => (
                        <li key={ index }>
                          <Link
                            to={ link.url }
                            className="footer-link"
                            title={ link.title }
                          >
                            {link.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              }
            )}
          </section>
          <div
            className="footer-industries"
            data-section="footer-industries-navigation"
          >
            <h4 className="footer-heading">
              {pageContext.options.industry_links.title}
            </h4>
            <section className="footer-wrap">
              {Object.entries(pageContext.options.industry_links.columns).map(
                (column: any[], i) => (
                  <ul key={ i } className="footer-col industries">
                    {column[1].links.map((industries_link: any) => (
                      <li key={ industries_link.link.url }>
                        <Link
                          to={ industries_link.link.url }
                          className="footer-link"
                          title={ industries_link.link.title }
                        >
                          {industries_link.link.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )
              )}

            </section>
          </div>
          <section className="footer-wrap">
            <div className="footer-col side-by-side">
              <div
                className="footer-block apps"
                data-section="footer-app-download"
              >
                <h4 className="footer-heading">Mobile Apps</h4>
                <div className="footer-apps">
                  {pageContext.options.download_mobile_apps.app_store
                    .map((icon: any, index) => (
                      <Link
                        key={ index }
                        href={ icon.app_store_url.url }
                        className="footer-link apps"
                      >
                        <img
                          loading="lazy"
                          src={ icon.app_store_image.url.source_url }
                          alt={ icon.app_store_image.url.alt_text }
                        />
                        <span className="is-sr-only">{icon.app_store_image.url.alt_text}</span>
                      </Link>
                    ))}
                </div>
              </div>
              <div
                className="footer-block social"
                data-section="footer-social-links"
              >
                <h4 className="footer-heading">Connect With Us</h4>
                <div className="footer-social">
                  {Object.entries(pageContext.options.social_icons_v2).map(
                    (icon: any[], index) => (
                      <Link
                        key={ index }
                        href={ icon[1].icon_url.url }
                        className="footer-link social"
                      >
                        <img
                          loading="lazy"
                          className="social-icon"
                          src={ icon[1].icon_image.url.source_url }
                          role="presentation"
                        />
                        <span className="is-sr-only">{icon[1].icon_image_alt}</span>
                      </Link>
                    )
                  )}
                </div>
              </div>
            </div>
            <div className="footer-col side-by-side">
              <div className="footer-block" data-section="footer-contact-us">
                <h4 className="footer-heading">
                  {pageContext.options.contact_us.footer_heading}
                </h4>
                <ul>
                  <li>
                    <button
                      type="button"
                      className="footer-link intercom-launch-demo"
                      onClick={ showScheduleDemoModal }
                      title={ pageContext.options.contact_us.sales_link.title }
                    >
                      {pageContext.options.contact_us.sales_link.title}
                    </button>
                  </li>
                  <li>
                    <Link
                      href={ pageContext.options.contact_us.support_link.url }
                      className="footer-link"
                      title={ pageContext.options.contact_us.support_link.title }
                    >
                      {pageContext.options.contact_us.support_link.title}
                    </Link>
                  </li>
                </ul>
              </div>
              <div className="footer-block address">
                <div className="footer-text footer-address">
                  <Wysiwyg
                    content={ pageContext.options.contact_us.footer_text }
                  />
                </div>
              </div>
            </div>
            <div className="footer-col newsletter">
              <div
                className="footer-newsletter"
                data-section="footer-crm-form-newsletter"
              >
                <h4 className="footer-heading">
                  {pageContext.options.subscription.subscribe_header}
                </h4>
                <div className="newsletter-form">
                  <CRMForm
                    formType="blogSubscription"
                    emailField
                    showIcons={ false }
                    fieldLayout="inline"
                    uniqueId="blogSubscriptionFooter"
                  />
                </div>
                <p className="footer-text">
                  {pageContext.options.subscription.subscription_copy}
                </p>
              </div>
            </div>
          </section>
          <section
            className="footer-wrap bottom"
            data-section="footer-legal-links"
          >
            <div className="footer-copyright">
              <p className="footer-text">
                &copy; {new Date().getFullYear()} When I Work, Inc.
              </p>
              <ul className="footer-legal">
                <li>
                  <span className="separator">&nbsp;|&nbsp;</span>
                  <Link
                    to={ pageContext.options.legal_links.privacy_policy.url }
                    className="legal-link"
                    title={ pageContext.options.legal_links.privacy_policy.title }
                  >
                    {pageContext.options.legal_links.privacy_policy.title}
                  </Link>
                  <span className="separator">&nbsp;|&nbsp;</span>
                </li>
                <li>
                  <Link
                    to={ pageContext.options.legal_links.terms_of_service.url }
                    className="legal-link"
                    title={ pageContext.options.legal_links.terms_of_service.title }
                  >
                    {pageContext.options.legal_links.terms_of_service.title}
                  </Link>
                </li>
              </ul>
            </div>
            <div className="footer-logo">
              <img
                loading="lazy"
                src="https://marketing-assets.wheniwork-production.com/2020/05/14134746/footer-wheniwork-logo.svg"
                alt="When I Work - Get Shift Done - Logo"
              />
            </div>
          </section>
        </div>
      </section>
      <StructuredData data={ structuredData } />
    </footer>
  );
};

export default Footer;